import { Moment } from 'moment'

import { ConfigItem } from 'Core/interfaces'
import { ConfigFilterItem } from 'Core/interfaces/filters'

export enum BillingItemFilterFields {
	startDate = 'startDate',
	endDate = 'endDate',
	status = 'status',
	billingAgency = 'billingAgency',
	serviceTeam = 'serviceTeam',
	searchPeriod = 'searchPeriod',
	isToOpenPreview = 'isToOpenPreview'
}

export enum BillingItemSearchSuggestions {
	billingItems = 'billingItems',
	companies = 'companies',
	customers = 'customers',
	serviceOccurrenceIds = 'serviceOccurrenceIds',
	itemIds = 'itemIds',
	isToOpenPreview = 'isToOpenPreview'
}

export enum BItemSearchSuggestions {
	searchById = 'searchById',
	searchByName = 'searchByName',
	searchByRefCode = 'searchByRefCode'
}

export enum BillingItemServiceTeamSearchFields {
	serviceTeamId = 'serviceTeamId'
}

export enum BillingItemBasicFields {
	id = 'id',
	item = 'item',
	items = 'items',
	itemId = 'itemId',
	itemRef = 'itemRef',
	unitId = 'unitId',
	notes = 'notes',
	units = 'units',
	reason = 'reason',
	status = 'status',
	refCode = 'refCode',
	unitPrice = 'unitPrice',
	billingItemId = 'billingItemId',
	customer = 'customer',
	billingFreqCode = 'billingFreqCode',
	comment = 'comment',
	currency = 'currency',
	billDate = 'billDate',
	quantity = 'quantity',
	itemDescription = 'itemDescription',
	serviceDate = 'serviceDate',
	totalPrice = 'totalPrice',
	serviceSite = 'serviceSite',
	totalCharge = 'totalCharge',
	transaction = 'transaction',
	billingAgency = 'billingAgency',
	billingItems = 'billingItems',
	unitOfMeasure = 'unitOfMeasure',
	primaryCharge = 'primaryCharge',
	chargeTypeCode = 'chargeTypeCode',
	billingQuantity = 'billingQuantity',
	customerInvoice = 'customerInvoice',
	modifiedUnitPrice = 'modifiedUnitPrice',
	serviceOccurrence = 'serviceOccurrence',
	chargeDescription = 'chargeDescription',
	supplierInvoiceId = 'supplierInvoiceId',
	serviceSiteId = 'serviceSiteId',
	serviceOccurrenceId = 'serviceOccurrenceId',
	supplierInvoice = 'supplierInvoice',
	customerUnitPrice = 'customerUnitPrice',
	customerInvoiceItem = 'customerInvoiceItem',
	totalChargesCurrency = 'totalChargesCurrency',
	modifiedExtendedPrice = 'modifiedExtendedPrice',
	customerExtendedPrice = 'customerExtendedPrice',
	modifiedBillingQuantity = 'modifiedBillingQuantity'
}

export enum BillingItemEditFormFields {
	status = 'status',
	newNote = 'newNote',
	billDate = 'billDate',
	currency = 'currency',
	itemRefCode = 'itemRefCode',
	originalQty = 'originalQty',
	adjustedQty = 'adjustedQty',
	primaryCharge = 'primaryCharge',
	originalNotes = 'originalNotes',
	chargeTypeCode = 'chargeTypeCode',
	chargeDescription = 'chargeDescription',
	originalUnitPrice = 'originalUnitPrice',
	adjustedUnitPrice = 'adjustedUnitPrice',
	customBillFreqCode = 'customBillFreqCode',
	originalUnitOfMeasure = 'originalUnitOfMeasure',
	originalExtendedPrice = 'originalExtendedPrice',
	adjustedExtendedPrice = 'adjustedExtendedPrice'
}

export enum BillingItemEditRequestFields {
	notes = 'notes',
	refCode = 'refCode',
	billDate = 'billDate',
	currencyId = 'currencyId',
	billingFrequencyCodeId = 'billingFrequencyCodeId',
	billingItemId = 'billingItemId',
	unitOfMeasureId = 'unitOfMeasureId',
	modifiedUnitPrice = 'modifiedUnitPrice',
	chargeTypeCodeId = 'chargeTypeCodeId',
	chargeDescription = 'chargeDescription',
	billingItemStatusId = 'billingItemStatusId',
	modifiedExtendedPrice = 'modifiedExtendedPrice',
	modifiedBillingQuantity = 'modifiedBillingQuantity',
	makeSupplierServiceOccurrenceCancelled = 'makeSupplierServiceOccurrenceCancelled'
}
export enum BillingItemChangeCustomerFields {
	customerId = 'customerId',
	billingItemIds = 'billingItemIds',
	comments = 'comment'
}

export enum BillingItemBuildFields {
	company = 'company'
}

export declare namespace BillingItem {
	interface Transaction {
		id: number
	}

	interface ServiceOccurrence {
		id: number
		customerOrderId: number
		actualServiceDate: BasicString
		customerOrderItemId: number
		transaction: Transaction | null
	}

	interface Wastestream extends Entity {
		managementMethod: Entity | null
	}

	interface Contract {
		id: number
		code: BasicString
	}

	interface Item extends Entity {
		contract: null
		wastestream: Wastestream | null
	}

	interface CustomerInvoice {
		customer: Entity
		customerInvoiceId: number
	}

	interface Basic {
		[BillingItemBasicFields.id]: number
		[BillingItemBasicFields.item]: Item | null
		[BillingItemBasicFields.notes]: BasicString
		[BillingItemBasicFields.status]: Entity | null
		[BillingItemBasicFields.refCode]: BasicString
		[BillingItemBasicFields.customer]: Entity | null
		[BillingItemBasicFields.billingFreqCode]: Entity | null
		[BillingItemBasicFields.currency]: Entity | null
		[BillingItemBasicFields.billDate]: BasicString
		[BillingItemBasicFields.serviceSite]: Entity | null
		[BillingItemBasicFields.totalCharge]: number | null
		[BillingItemBasicFields.billingAgency]: Entity | null
		[BillingItemBasicFields.unitOfMeasure]: Entity | null
		[BillingItemBasicFields.primaryCharge]: boolean
		[BillingItemBasicFields.chargeTypeCode]: Entity | null
		[BillingItemBasicFields.billingQuantity]: number
		[BillingItemBasicFields.customerInvoice]: CustomerInvoice | null
		[BillingItemBasicFields.modifiedUnitPrice]: number | null
		[BillingItemBasicFields.serviceOccurrence]: ServiceOccurrence | null
		[BillingItemBasicFields.chargeDescription]: BasicString
		[BillingItemBasicFields.customerUnitPrice]: number | null
		[BillingItemBasicFields.supplierInvoice]: {
			id: number | null
			scanRecordId: number | null
			status: Entity | null
		} | null
		[BillingItemBasicFields.customerInvoiceItem]: Entity | null
		[BillingItemBasicFields.totalChargesCurrency]: BasicString
		[BillingItemBasicFields.modifiedExtendedPrice]: number | null
		[BillingItemBasicFields.customerExtendedPrice]: number | null
		[BillingItemBasicFields.modifiedBillingQuantity]: number | null
	}

	interface Detailed extends Basic {}

	interface BillingItemDetail {
		[BillingItemBasicFields.id]: number
		[BillingItemBasicFields.serviceOccurrenceId]: number
		[BillingItemBasicFields.chargeDescription]: BasicString
		[BillingItemBasicFields.quantity]: number | null
		[BillingItemBasicFields.totalPrice]: number | null
		[BillingItemBasicFields.serviceDate]: BasicString
		[BillingItemBasicFields.item]: Entity
	}

	interface BillingItemDetailOption extends Omit<BillingItemDetail, 'item'> {
		[BillingItemBasicFields.item]: LabelValue<number> | null
	}

	interface BillingItemWithPrice {
		[BillingItemBasicFields.id]: number
		[BillingItemBasicFields.billingItemId]: number
		[BillingItemBasicFields.itemId]: number
		[BillingItemBasicFields.itemRef]: BasicString
		[BillingItemBasicFields.itemDescription]: BasicString
		[BillingItemBasicFields.quantity]: number
		[BillingItemBasicFields.units]: Entity
		[BillingItemBasicFields.unitPrice]: number
		[BillingItemBasicFields.totalPrice]: number
	}

	namespace ServiceTeamSearch {
		interface Config {
			[BillingItemServiceTeamSearchFields.serviceTeamId]: ConfigItem<BillingItemServiceTeamSearchFields>
		}
	}

	namespace Search {
		interface Config {
			[BillingItemSearchSuggestions.billingItems]: ConfigItem<BillingItemSearchSuggestions>
			[BillingItemSearchSuggestions.companies]: ConfigItem<BillingItemSearchSuggestions>
			[BillingItemSearchSuggestions.customers]: ConfigItem<BillingItemSearchSuggestions>
			[BillingItemSearchSuggestions.serviceOccurrenceIds]: ConfigItem<BillingItemSearchSuggestions>
			[BillingItemSearchSuggestions.itemIds]: ConfigItem<BillingItemSearchSuggestions>
		}

		interface Result {
			[BillingItemSearchSuggestions.billingItems]: []
			[BillingItemSearchSuggestions.customers]: Entity[]
			[BillingItemSearchSuggestions.serviceOccurrenceIds]: []
			[BillingItemSearchSuggestions.itemIds]: Entity[]
		}
	}

	namespace Filter {
		interface Config {
			[BillingItemFilterFields.startDate]: ConfigFilterItem<string, null>
			[BillingItemFilterFields.endDate]: ConfigFilterItem<string, null>
			[BillingItemFilterFields.status]: ConfigFilterItem<string, string>
			[BillingItemFilterFields.billingAgency]: ConfigFilterItem<string, string>
			[BillingItemFilterFields.serviceTeam]: ConfigFilterItem<string, string>
			[BillingItemFilterFields.searchPeriod]: ConfigFilterItem<string, null>
			[BillingItemFilterFields.isToOpenPreview]: ConfigFilterItem<string, null>
		}
	}

	namespace Request {
		interface Edit {
			[BillingItemEditRequestFields.notes]: string
			[BillingItemEditRequestFields.refCode]?: BasicString
			[BillingItemEditRequestFields.billDate]?: BasicString
			[BillingItemEditRequestFields.currencyId]: number | null
			[BillingItemEditRequestFields.billingFrequencyCodeId]: number | null
			[BillingItemEditRequestFields.billingItemId]: number
			[BillingItemEditRequestFields.unitOfMeasureId]: number | null
			[BillingItemEditRequestFields.chargeTypeCodeId]: number | null
			[BillingItemEditRequestFields.modifiedUnitPrice]: number | null
			[BillingItemEditRequestFields.chargeDescription]: BasicString
			[BillingItemEditRequestFields.billingItemStatusId]: number | null
			[BillingItemEditRequestFields.modifiedExtendedPrice]: number | null
			[BillingItemEditRequestFields.modifiedBillingQuantity]: number | null
			[BillingItemEditRequestFields.makeSupplierServiceOccurrenceCancelled]: boolean
		}
	}

	namespace Form {
		namespace Edit {
			interface Basic {
				[BillingItemEditFormFields.status]: LabelValue | null
				[BillingItemEditFormFields.newNote]: string
				[BillingItemEditFormFields.currency]: LabelValue | BasicString
				[BillingItemEditFormFields.billDate]: Moment | BasicString
				[BillingItemEditFormFields.itemRefCode]: string
				[BillingItemEditFormFields.originalQty]: string
				[BillingItemEditFormFields.adjustedQty]: BasicString
				[BillingItemEditFormFields.primaryCharge]: boolean | null
				[BillingItemEditFormFields.originalNotes]: BasicString
				[BillingItemEditFormFields.chargeTypeCode]: LabelValue | BasicString
				[BillingItemEditFormFields.chargeDescription]: BasicString
				[BillingItemEditFormFields.originalUnitPrice]: BasicString
				[BillingItemEditFormFields.adjustedUnitPrice]: BasicString
				[BillingItemEditFormFields.customBillFreqCode]: LabelValue | BasicString
				[BillingItemEditFormFields.originalUnitOfMeasure]: LabelValue | BasicString
				[BillingItemEditFormFields.originalExtendedPrice]: BasicString
				[BillingItemEditFormFields.adjustedExtendedPrice]: BasicString
			}

			interface Valid extends Basic {
				[BillingItemEditFormFields.status]: LabelValue
				[BillingItemEditFormFields.newNote]: NonNullable<BasicString>
				[BillingItemEditFormFields.currency]: LabelValue | NonNullable<BasicString>
				[BillingItemEditFormFields.chargeTypeCode]: LabelValue | NonNullable<BasicString>
				[BillingItemEditFormFields.chargeDescription]: NonNullable<BasicString>
				[BillingItemEditFormFields.originalUnitOfMeasure]:
					| LabelValue
					| NonNullable<BasicString>
			}
		}
	}

	namespace ChangeCustomer {
		namespace Customer {
			namespace Form {
				interface Edit {
					[BillingItemChangeCustomerFields.customerId]: LabelValue | null
					[BillingItemChangeCustomerFields.comments]: BasicString
				}

				interface Valid extends Edit {
					[BillingItemChangeCustomerFields.customerId]: LabelValue
				}
			}

			namespace Request {
				interface Edit {
					[BillingItemChangeCustomerFields.comments]?: BasicString
					[BillingItemChangeCustomerFields.customerId]: number
					[BillingItemChangeCustomerFields.billingItemIds]: number[]
				}
			}

			namespace Search {
				interface Config {
					[BillingItemSearchSuggestions.customers]: ConfigItem<BillingItemSearchSuggestions>
				}

				interface Result {
					[BillingItemSearchSuggestions.customers]: Entity[]
				}
			}
		}

		namespace ServiceSite {
			interface BillingItem {
				[BillingItemBasicFields.itemId]: number
				[BillingItemBasicFields.billingItemId]: number
			}

			namespace Form {
				interface Edit {
					[BillingItemBasicFields.serviceSiteId]: LabelValue<number> | null
					[BillingItemBasicFields.reason]: BasicString
					[BillingItemBasicFields.comment]: BasicString
					[BillingItemBasicFields.billingItems]: BillingItemDetailOption[]
				}
			}

			namespace Request {
				interface Edit extends BillingItem {}
			}

			namespace Search {
				interface Config {
					[BItemSearchSuggestions.searchById]: ConfigItem<BItemSearchSuggestions>
					[BItemSearchSuggestions.searchByName]: ConfigItem<BItemSearchSuggestions>
					[BItemSearchSuggestions.searchByRefCode]: ConfigItem<BItemSearchSuggestions>
				}

				interface Result {
					[BItemSearchSuggestions.searchById]: Entity[]
					[BItemSearchSuggestions.searchByName]: Entity[]
					[BItemSearchSuggestions.searchByRefCode]: CodeEntity[]
				}
			}
		}

		namespace EditCharges {
			interface BasicEditItem extends Omit<BillingItemWithPrice, 'units'> {
				unitId: number
			}

			namespace Form {
				interface Edit {
					[BillingItemBasicFields.items]: BasicEditItem[]
				}
			}

			namespace Request {
				interface Edit {
					[BillingItemBasicFields.billingItems]: BasicEditItem[]
					[BillingItemBasicFields.serviceSiteId]: number
					[BillingItemBasicFields.reason]: number
					[BillingItemBasicFields.comment]: BasicString
				}
			}
		}
	}

	namespace Build {
		namespace Form {
			interface Basic {
				[BillingItemBuildFields.company]: number | null
			}

			interface Valid extends Basic {
				[BillingItemBuildFields.company]: number
			}
		}

		namespace Request {
			interface Generic {
				countOfBillingItemsToBeCreated: number
			}
		}
	}

	interface SearchPeriod {
		name: string
		to: string
		from: string
	}

	namespace Validate {
		interface Basic {
			notCanceledServiceOccurrences: {
				serviceOccurrenceId: number
				supplierServiceOccurrenceId: number
			}[]
			includedServiceOccurrences: {
				serviceOccurrenceId: number
				supplierServiceOccurrenceId: number
			}[]
		}

		interface Form {
			[BillingItemEditRequestFields.makeSupplierServiceOccurrenceCancelled]: boolean
		}

		interface Request {
			billingItemIds: number[]
			statusId?: number | null
		}
	}
}
